"use client";

import {
    ExternalLink,
    Layers,
    Loader2,
    MoreHorizontal,
    RefreshCw,
    Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import toast from "@/lib/toast";
import { t, getDirectionFromText, type Locale } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";
import { useStartAvatarConsent } from "../api/useAvatarConsent";
import type { AvatarWithLooks } from "../types";
import { resolveDefaultLook, avatarSupportsV } from "../types";
import EngineQualityBadge from "./engineQualityBadge";

interface AvatarCardProps {
    avatar: AvatarWithLooks;
    onSelect?: (avatar: AvatarWithLooks) => void;
    onDelete?: (avatar: AvatarWithLooks) => void;
    onRefresh?: (avatar: AvatarWithLooks) => void;
    onDragStart?: (e: React.DragEvent, avatar: AvatarWithLooks) => void;
    className?: string;
}

function statusLabel(status: string, locale: Locale): string {
    switch (status) {
        case "completed":
            return t("avatars.card.status.ready", locale);
        case "pending_consent":
            return t("avatars.card.status.pending-consent", locale);
        case "processing":
            return t("avatars.card.status.processing", locale);
        case "failed":
            return t("avatars.card.status.failed", locale);
        default:
            return status;
    }
}

export default function AvatarCard({
    avatar,
    onSelect,
    onDelete,
    onRefresh,
    onDragStart,
    className,
}: AvatarCardProps) {
    const { locale, dir } = useLocale();
    const look = resolveDefaultLook(avatar);
    const previewUrl = avatar.previewImageUrl ?? look?.previewImageUrl ?? null;
    const statusText = statusLabel(avatar.status, locale);
    const isReady = avatar.status === "completed";
    const needsConsent = avatar.status === "pending_consent";
    // Avatar V (higher quality) is enabled by HeyGen only after the first
    // render; flag ready avatars whose looks don't advertise it yet.
    const lacksV =
        isReady && avatar.looks.length > 0 && !avatarSupportsV(avatar);
    const startConsent = useStartAvatarConsent();

    const openConsent = async () => {
        // Regenerate a fresh URL on every click — HeyGen's consent tokens are
        // short-lived, so reopening yesterday's URL would otherwise 404.
        try {
            const { consentUrl } = await startConsent.mutateAsync({
                id: avatar.id,
                rerouteUrl:
                    typeof window !== "undefined"
                        ? window.location.href
                        : undefined,
            });
            window.open(consentUrl, "_blank", "noopener,noreferrer");
        } catch {
            // Hook surfaces the error toast. Fall back to the stored URL if we
            // have one — better stale than nothing.
            if (avatar.consentUrl) {
                window.open(avatar.consentUrl, "_blank", "noopener,noreferrer");
            } else {
                toast.error(t("avatars.toast.consent.error", locale), {
                    keyName: "toast.error.consent",
                });
            }
        }
    };

    return (
        <div
            draggable={isReady && !!onDragStart}
            onDragStart={(e) => isReady && onDragStart?.(e, avatar)}
            onClick={() => onSelect?.(avatar)}
            className={cn(
                "group relative w-full rounded-lg border bg-background overflow-hidden",
                isReady && "cursor-pointer hover:ring-2 hover:ring-ring/50",
                className
            )}
        >
            <div className="aspect-square bg-muted relative">
                {previewUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                        src={previewUrl}
                        alt={avatar.name}
                        className={cn(
                            "absolute inset-0 w-full h-full object-cover",
                            !isReady && "opacity-60"
                        )}
                    />
                ) : (
                    <div
                        className={cn(
                            "absolute inset-0 flex items-center justify-center text-xs text-muted-foreground",
                            !isReady && "opacity-60"
                        )}
                    >
                        {t("avatars.card.no-preview", locale)}
                    </div>
                )}
                {!isReady && (
                    <button
                        type="button"
                        onClick={
                            needsConsent
                                ? (e) => {
                                      e.stopPropagation();
                                      openConsent();
                                  }
                                : (e) => e.stopPropagation()
                        }
                        title={
                            needsConsent
                                ? t("avatars.card.sign-consent", locale)
                                : undefined
                        }
                        className={cn(
                            "absolute top-1 start-1 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none",
                            "bg-black/60 text-white backdrop-blur-sm ring-1 ring-white/15 shadow-sm",
                            needsConsent &&
                                "cursor-pointer hover:bg-primary hover:ring-primary",
                            !needsConsent && "cursor-default"
                        )}
                    >
                        {needsConsent &&
                            (startConsent.isPending ? (
                                <Loader2 className="size-2.5 animate-spin" />
                            ) : (
                                <ExternalLink className="size-2.5" />
                            ))}
                        {statusText}
                    </button>
                )}
                {avatar.looks.length > 0 && (
                    <div className="absolute bottom-1 end-1 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium text-white bg-black/60 backdrop-blur-sm ring-1 ring-white/15 shadow-sm">
                        <Layers className="size-3" />
                        {avatar.looks.length}
                    </div>
                )}
                {lacksV && <EngineQualityBadge />}
                <div className="absolute top-1 end-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100">
                    <DropdownMenu dir={dir}>
                        <DropdownMenuTrigger asChild>
                            <Button
                                size="icon"
                                variant="ghost"
                                className="size-6 rounded-full bg-black/60 text-white hover:bg-black/75 hover:text-white backdrop-blur-sm ring-1 ring-white/15 shadow-sm"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <MoreHorizontal className="size-3.5" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="min-w-0">
                            {needsConsent && (
                                <DropdownMenuItem
                                    className="text-xs py-1"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openConsent();
                                    }}
                                >
                                    <ExternalLink className="!size-3" />
                                    {t("avatars.card.sign-consent", locale)}
                                </DropdownMenuItem>
                            )}
                            {onRefresh && (
                                <DropdownMenuItem
                                    className="text-xs py-1"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onRefresh(avatar);
                                    }}
                                >
                                    <RefreshCw className="!size-3" />
                                    {t("avatars.card.refresh-status", locale)}
                                </DropdownMenuItem>
                            )}
                            {onDelete && (
                                <DropdownMenuItem
                                    className="text-xs py-1 text-destructive"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDelete(avatar);
                                    }}
                                >
                                    <Trash2 className="!size-3" />
                                    {t("avatars.card.delete", locale)}
                                </DropdownMenuItem>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
            <div
                dir={getDirectionFromText(avatar.name)}
                className="p-1.5 text-[11px] w-full truncate text-start"
            >
                {avatar.name}
            </div>
        </div>
    );
}
